import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    List,
    ListItem,
    ListItemText,
    Chip,
    CircularProgress,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Tooltip,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faEdit } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useApplicationContext } from '../../ApplicationContext';
import { LicenseOptionDto, LicenseOptionType, CreateLicenseOptionRequest } from '../../apiClient/Api';

interface LicenseOptionsOverviewDialogProps {
    open: boolean;
    onClose: () => void;
    onDataChanged?: () => void;
}

/**
 * Dialog zur Visualisierung aller Lizenzoptionen und ihrer Verwendungen
 */
export const LicenseOptionsOverviewDialog: React.FC<LicenseOptionsOverviewDialogProps> = ({
    open,
    onClose,
    onDataChanged
}) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const context = useApplicationContext();
    const [loading, setLoading] = useState(false);
    const [options, setOptions] = useState<LicenseOptionDto[]>([]);
    const [usages, setUsages] = useState<Record<string, any>>({});
    
    // Edit dialog states
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingOption, setEditingOption] = useState<LicenseOptionDto | null>(null);
    const [editName, setEditName] = useState('');
    const [editIdentifier, setEditIdentifier] = useState('');
    const [editType, setEditType] = useState<LicenseOptionType>(LicenseOptionType.String);
    const [editDefaultValue, setEditDefaultValue] = useState('');
    const [editDescription, setEditDescription] = useState('');
    const [editDisplayOrder, setEditDisplayOrder] = useState(0);
    const [editIsActive, setEditIsActive] = useState(true);
    const [editIsVisibleToCustomer, setEditIsVisibleToCustomer] = useState(true);

    /**
     * Loads all license options and their usages
     */
    const loadOptionsAndUsages = async () => {
        setLoading(true);
        try {
            // Load all license options
            const optionsResponse = await context.api.api.licenseGetLicenseOptions({ activeOnly: false });
            setOptions(optionsResponse.data);

            // Load usage info for each option
            const usagePromises = optionsResponse.data.map(async (option) => {
                if (!option.id) return null;
                try {
                    const usageResponse = await context.api.api.licenseGetLicenseOptionUsages(option.id);
                    return { optionId: option.id, usage: usageResponse.data };
                } catch (e) {
                    return null;
                }
            });

            const usageResults = await Promise.all(usagePromises);
            const usageMap: Record<string, any> = {};
            usageResults.forEach(result => {
                if (result) {
                    usageMap[result.optionId] = result.usage;
                }
            });
            setUsages(usageMap);
        } catch (e) {
            context.apiError(e);
        }
        setLoading(false);
    };

    useEffect(() => {
        if (open) {
            loadOptionsAndUsages();
        }
    }, [open]);

    /**
     * Opens the edit dialog for a license option
     */
    const handleOpenEditDialog = (option: LicenseOptionDto) => {
        setEditingOption(option);
        setEditName(option.commonName || '');
        setEditIdentifier(option.identifier || '');
        // Ensure type is properly set, default to String if undefined/null
        setEditType(option.type !== undefined && option.type !== null ? option.type : LicenseOptionType.String);
        setEditDefaultValue(option.defaultValue || '');
        setEditDescription(option.description || '');
        setEditDisplayOrder(option.displayOrder || 0);
        setEditIsActive(option.isActive ?? true);
        setEditIsVisibleToCustomer(option.isVisibleToCustomer ?? true);
        setEditDialogOpen(true);
    };

    /**
     * Saves the edited license option
     */
    const handleSaveEdit = async () => {
        if (!editingOption?.id) return;

        try {
            const request: CreateLicenseOptionRequest = {
                commonName: editName,
                identifier: editIdentifier,
                type: editType,
                defaultValue: editDefaultValue || null,
                description: editDescription || null,
                isActive: editIsActive,
                displayOrder: editDisplayOrder,
                isVisibleToCustomer: editIsVisibleToCustomer
            };

            await context.api.api.licenseUpdateLicenseOption(editingOption.id, request);
            enqueueSnackbar(t('fileTypeEdit.licenseOptions.overview.updateSuccess'), { variant: 'success' });
            
            setEditDialogOpen(false);
            await loadOptionsAndUsages();
            
            // Notify parent that data has changed so it can reload
            if (onDataChanged) {
                onDataChanged();
            }
        } catch (e) {
            context.apiError(e);
        }
    };

    /**
     * Returns the type label for a license option type
     */
    const getTypeLabel = (type?: number): string => {
        switch (type) {
            case 0: return 'Number';
            case 1: return 'Boolean';
            case 2: return 'String';
            case 3: return 'Date';
            default: return 'Unknown';
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                {t('fileTypeEdit.licenseOptions.overview.title')}
            </DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box sx={{ mt: 2 }}>
                        {options.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                {t('fileTypeEdit.licenseOptions.overview.noOptions')}
                            </Typography>
                        ) : (
                            <>
                                {options.map((option, index) => {
                                    const usage = usages[option.id!];
                                    const usageCount = usage?.usageCount || 0;
                                    const usedByFileTypes = usage?.usedByFileTypes || [];

                                    return (
                                        <Box key={option.id} sx={{ mb: 3 }}>
                                            {index > 0 && <Divider sx={{ mb: 3 }} />}
                                            
                                            {/* Header */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                                <Box sx={{ flex: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography variant="h6">
                                                            {option.commonName}
                                                        </Typography>
                                                        <Tooltip title={t('fileTypeEdit.licenseOptions.overview.editOption')}>
                                                            <Button 
                                                                size="small" 
                                                                onClick={() => handleOpenEditDialog(option)}
                                                                startIcon={<FontAwesomeIcon icon={faEdit} />}
                                                            >
                                                                {t('fileTypeEdit.licenseOptions.overview.edit')}
                                                            </Button>
                                                        </Tooltip>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                                                        <Chip 
                                                            label={option.identifier} 
                                                            size="small" 
                                                            variant="outlined"
                                                        />
                                                        <Chip 
                                                            label={getTypeLabel(option.type)} 
                                                            size="small" 
                                                            color="primary"
                                                            variant="outlined"
                                                        />
                                                        {option.defaultValue && (
                                                            <Chip 
                                                                label={`${t('fileTypeEdit.licenseOptions.overview.globalDefault')}: ${option.defaultValue}`}
                                                                size="small" 
                                                                color="success"
                                                                variant="outlined"
                                                            />
                                                        )}
                                                        {option.isVisibleToCustomer === false && (
                                                            <Chip 
                                                                label={t('fileTypeEdit.licenseOptions.hiddenFromCustomer')}
                                                                size="small" 
                                                                color="warning"
                                                                variant="outlined"
                                                            />
                                                        )}
                                                    </Box>
                                                    {option.description && (
                                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                            {option.description}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                <Chip 
                                                    label={`${usageCount} ${t('fileTypeEdit.licenseOptions.overview.usages')}`}
                                                    color={usageCount > 0 ? 'primary' : 'default'}
                                                    size="medium"
                                                />
                                            </Box>

                                            {/* Usage Table */}
                                            {usedByFileTypes.length > 0 ? (
                                                <TableContainer component={Paper} variant="outlined">
                                                    <Table size="small">
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell><strong>{t('fileTypeEdit.licenseOptions.overview.fileType')}</strong></TableCell>
                                                                <TableCell align="center"><strong>{t('fileTypeEdit.required')}</strong></TableCell>
                                                                <TableCell><strong>{t('fileTypeEdit.licenseOptions.overview.defaultValue')}</strong></TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {usedByFileTypes.map((fileType: any) => (
                                                                <TableRow key={fileType.fileTypeId} hover>
                                                                    <TableCell>
                                                                        <Typography variant="body2">
                                                                            {fileType.fileTypeName}
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell align="center">
                                                                        {fileType.isRequired ? (
                                                                            <Chip 
                                                                                label={t('fileTypeEdit.licenseOptions.overview.yes')} 
                                                                                color="error" 
                                                                                size="small" 
                                                                            />
                                                                        ) : (
                                                                            <Chip 
                                                                                label={t('fileTypeEdit.licenseOptions.overview.no')} 
                                                                                size="small" 
                                                                                variant="outlined"
                                                                            />
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {fileType.fileTypeSpecificDefault ? (
                                                                            <Tooltip title={t('fileTypeEdit.licenseOptions.overview.overridesGlobal')}>
                                                                                <Chip 
                                                                                    label={fileType.fileTypeSpecificDefault}
                                                                                    size="small"
                                                                                    color="warning"
                                                                                />
                                                                            </Tooltip>
                                                                        ) : option.defaultValue ? (
                                                                            <Typography variant="body2" color="text.secondary">
                                                                                {option.defaultValue}
                                                                            </Typography>
                                                                        ) : (
                                                                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                                                -
                                                                            </Typography>
                                                                        )}
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            ) : (
                                                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center' }}>
                                                        {t('fileTypeEdit.licenseOptions.overview.notUsed')}
                                                    </Typography>
                                                </Paper>
                                            )}
                                        </Box>
                                    );
                                })}
                            </>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>
                    {t('fileTypeEdit.cancel')}
                </Button>
            </DialogActions>

            {/* Edit License Option Dialog */}
            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    {t('fileTypeEdit.licenseOptions.overview.editOption')}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                required
                                label={t('fileTypeEdit.licenseOptions.commonName')}
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                required
                                label={t('fileTypeEdit.licenseOptions.identifier')}
                                value={editIdentifier}
                                onChange={(e) => setEditIdentifier(e.target.value)}
                                helperText={t('fileTypeEdit.licenseOptions.identifierHelper')}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth required>
                                <InputLabel>{t('fileTypeEdit.licenseOptions.type')}</InputLabel>
                                <Select
                                    value={editType}
                                    onChange={(e) => setEditType(e.target.value as LicenseOptionType)}
                                    label={t('fileTypeEdit.licenseOptions.type')}
                                >
                                    <MenuItem value={LicenseOptionType.Number}>Number</MenuItem>
                                    <MenuItem value={LicenseOptionType.Boolean}>Boolean</MenuItem>
                                    <MenuItem value={LicenseOptionType.String}>String</MenuItem>
                                    <MenuItem value={LicenseOptionType.Date}>Date</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                label={t('fileTypeEdit.licenseOptions.defaultValue')}
                                value={editDefaultValue}
                                onChange={(e) => setEditDefaultValue(e.target.value)}
                                helperText={t('fileTypeEdit.licenseOptions.defaultValueHelper')}
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                fullWidth
                                label={t('fileTypeEdit.licenseOptions.description')}
                                value={editDescription}
                                onChange={(e) => setEditDescription(e.target.value)}
                                multiline
                                rows={2}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                type="number"
                                label={t('fileTypeEdit.licenseOptions.overview.displayOrder')}
                                value={editDisplayOrder}
                                onChange={(e) => setEditDisplayOrder(parseInt(e.target.value))}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={editIsActive}
                                        onChange={(e) => setEditIsActive(e.target.checked)}
                                    />
                                }
                                label={t('fileTypeEdit.licenseOptions.overview.isActive')}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={editIsVisibleToCustomer}
                                        onChange={(e) => setEditIsVisibleToCustomer(e.target.checked)}
                                    />
                                }
                                label={t('fileTypeEdit.licenseOptions.visibleToCustomer')}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialogOpen(false)}>
                        {t('fileTypeEdit.cancel')}
                    </Button>
                    <Button
                        onClick={handleSaveEdit}
                        variant="contained"
                        disabled={!editName || !editIdentifier}
                    >
                        {t('fileTypeEdit.save')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
};
